import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase client — single instance for the whole app.
 *
 * Reads credentials from Vite env vars (`VITE_SUPABASE_URL` +
 * `VITE_SUPABASE_ANON_KEY`). If either is missing, `supabase` is null
 * and the app runs in **local-only mode** — same behaviour as before
 * the backend kicked in (localStorage save, no cross-device, no chat
 * sync). This makes the dev loop work without forcing every contributor
 * to spin up a Supabase project.
 *
 * See `BACKEND_SETUP.md` at repo root for the exact env-var format.
 */

/**
 * Read through a defensive `?? {}`.
 *
 * `import.meta.env` is Vite's, and it is UNDEFINED under plain Node — so the
 * bare `import.meta.env.VITE_…` threw the moment anything in a probe's import
 * chain reached this module. Two probes (`check_dot`, `check_dot2`) went from
 * passing to un-runnable without a line of their own changing, purely because
 * the graph under `classGear` grew to include the store.
 *
 * A probe that cannot run reads as coverage, so the fix belongs here rather
 * than in each probe: this module stays importable from Node, and every
 * `.verify/check_*.ts` can import anything in src without a shim.
 */
const ENV = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}

const url = ENV.VITE_SUPABASE_URL
const anonKey = ENV.VITE_SUPABASE_ANON_KEY

/**
 * `?offline=1` on LOCALHOST forces local-only mode.
 *
 * The automated drivers in `.verify/` load the dev server, which carries real
 * credentials — so every run signed in as a guest, completed character
 * creation, and wrote a REAL `profiles` row. Those rows then surfaced as
 * duellable PvP opponents ("still getting players that don't exist") and as
 * junk in the live database. Filtering them client-side treated the symptom;
 * this is the cause.
 *
 * Restricted to localhost deliberately. It is a testing affordance, not a
 * feature: on a deployed build the flag is ignored, so it cannot be used to
 * dodge cloud save or server-authoritative checks.
 */
const isLocalHost = typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
const forcedOffline = isLocalHost
  && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('offline') === '1'

if (forcedOffline) {
  console.info('[supabase] ?offline=1 on localhost — running local-only, nothing will be written to the backend.')
}

export const supabase: SupabaseClient | null = !forcedOffline && url && anonKey
  ? createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // Use localStorage explicitly — matches the rest of our persistence.
        storage: window.localStorage,
      },
    })
  : null

/** True when the Supabase client is configured. Use to gate auth/sync code paths. */
export const isBackendConfigured = supabase !== null
